"use client";

import { Hand, Leaf, Waves, Wind } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const principles = [{
  icon: Leaf,
  number: "01 // PRECISION",
  title: "Surgical Pruning",
  description: "We don't just cut; we curate. Every incision is calculated to promote long-term structural integrity and the natural aesthetic of your canopy."
}, {
  icon: Wind,
  number: "02 // SAFETY",
  title: "Zero-Risk Protocol",
  description: "Utilizing advanced rigging and aerial lifts, we navigate complex residential environments with clinical safety, protecting your home and our crew."
}, {
  icon: Hand,
  number: "03 // LEGACY",
  title: "DFW Heritage",
  description: "As local arborists, we understand the unique soil and climate of North Texas. We treat every tree as a multi-generational asset to your property."
}, {
  icon: Waves,
  number: "04 // RESPONSE",
  title: "Rapid Recovery",
  description: "When Texas storms strike, our team is mobilized for immediate hazard mitigation, clearing debris and stabilizing damaged structures with urgency."
}];
function PrincipleColumn({
  principle,
  index
}: {
  principle: (typeof principles)[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const Icon = principle.icon;
  return <div ref={ref} data-index={index} className="relative px-6 py-10 md:px-8 md:py-0 transition-all duration-700 ease-out" style={{
    transitionDelay: `${index * 120}ms`,
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(24px)"
  }}>
      <Icon className="size-12 mb-8 stroke-[1.5] text-primary" aria-hidden="true" />
      <div className="text-[0.75rem] tracking-[0.2em] uppercase text-muted-foreground mb-5 font-mono">
        {principle.number}
      </div>
      <h3 className="font-default font-semibold text-xl md:text-2xl leading-tight text-foreground mb-5">
        {principle.title}
      </h3>
      <p className="text-[1rem] leading-relaxed text-muted-foreground font-default">
        {principle.description}
      </p>
    </div>;
}
export default function TheArborProDifference() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2744" id="the-arbor-pro-difference" className="dark bg-background py-24 md:py-32 lg:py-40 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div ref={headerRef} className="text-center max-w-4xl mx-auto mb-20 md:mb-28 transition-all duration-700 ease-out" style={{
        opacity: headerVisible ? 1 : 0,
        transform: headerVisible ? "translateY(0)" : "translateY(24px)"
      }}>
          <div className="text-[0.875rem] tracking-[0.2em] uppercase text-primary mb-8 font-mono">
            THE · ARBOR · PRO · STANDARD
          </div>
          <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-[64px] leading-[0.95] text-foreground tracking-tighter">
            Surgical{" "}
            <span className="text-primary">
              precision
            </span>
            .
            <br className="hidden sm:block" /> Built for{" "}
            <span className="text-primary">longevity</span>.
          </h2>
        </div>

        <div className="bg-card border border-border/20 rounded-md">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-border/20">
            {principles.map((principle, i) => <div key={principle.title} data-index={i} className={i >= 2 ? "sm:border-t sm:border-border/20 lg:border-t-0" : ""}>
                <PrincipleColumn principle={principle} index={i} />
              </div>)}
          </div>
        </div>
      </div>

      <div ref={imageRef} className="mt-20 md:mt-28 lg:mt-32 transition-all duration-1000 ease-out" style={{
      opacity: imageVisible ? 1 : 0,
      transform: imageVisible ? "translateY(0)" : "translateY(32px)"
    }}>
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/IMG_5151.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/IMG_5151.jpg-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/IMG_5151.jpg-wvc-sizes" alt="Professional arborist performing a precision cut on a large oak tree in a residential Fort Worth backyard" loading="lazy" decoding="async" className="w-full h-[50vh] md:h-[65vh] lg:h-[75vh] object-cover grayscale contrast-125" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 mt-6">
          <p className="font-mono text-xs md:text-sm text-muted-foreground uppercase tracking-widest font-default">
            // surgical accuracy maintained across all residential canopy projects in north texas.
          </p>
        </div>
      </div>
    </section>;
}